import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { FileSpreadsheet } from 'lucide-react';

interface BusinessReportExcelProps {
  title: string;
  content: string;
  companyName?: string;
}

/**
 * Component for generating Excel business reports with professional formatting
 */
export const BusinessReportExcel: React.FC<BusinessReportExcelProps> = ({ 
  title, 
  content, 
  companyName = 'Su Empresa' 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  // Process the content into structured data for Excel
  const processContent = (text: string): {
    title: string;
    sections: Array<{
      isHeading: boolean;
      isList: boolean;
      content: string;
    }>;
  } => {
    if (!text || text.trim() === '') {
      return {
        title: title || 'Documento Empresarial',
        sections: [{ isHeading: false, isList: false, content: 'No se pudo recuperar texto del documento.' }]
      };
    }

    // Split text into lines and filter out empty ones
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');

    // Use provided title or first line as document title
    const documentTitle = title || (lines.length > 0 ? lines[0] : 'Documento Empresarial');

    // Process content lines (skip first line if it's being used as title)
    const contentLines = title ? lines : (lines.length > 1 ? lines.slice(1) : []);

    // Group lines into paragraphs and identify headings and lists
    const sections: Array<{ isHeading: boolean; isList: boolean; content: string }> = [];
    let currentParagraph = '';

    contentLines.forEach(line => {
      const trimmedLine = line.trim();

      // If empty line and we have content, push the current paragraph
      if (trimmedLine === '') {
        if (currentParagraph !== '') {
          sections.push({
            isHeading: false,
            isList: false,
            content: currentParagraph
          });
          currentParagraph = '';
        }
        return;
      }

      // Check if it's a heading (short line without ending punctuation)
      if (
        trimmedLine.length < 50 &&
        !trimmedLine.endsWith('.') &&
        !trimmedLine.endsWith('?') &&
        !trimmedLine.endsWith('!')
      ) {
        // If we have content, push it first
        if (currentParagraph !== '') {
          sections.push({
            isHeading: false,
            isList: false,
            content: currentParagraph
          });
          currentParagraph = '';
        }

        // Add the heading
        sections.push({
          isHeading: true,
          isList: false,
          content: trimmedLine
        });
        return;
      }

      // Check if it's a list item
      if (trimmedLine.match(/^[\d\-\*\•][\.\)\-\s]/)) {
        // If we have content, push it first
        if (currentParagraph !== '') {
          sections.push({
            isHeading: false,
            isList: false,
            content: currentParagraph
          });
          currentParagraph = '';
        }

        // Add the list item
        sections.push({
          isHeading: false,
          isList: true,
          content: trimmedLine.replace(/^[\d\-\*\•][\.\)\-\s]/, '')
        });
        return;
      }

      // Regular paragraph content
      if (currentParagraph !== '') {
        currentParagraph += ' ';
      }
      currentParagraph += trimmedLine;
    });

    // Add any remaining paragraph
    if (currentParagraph !== '') {
      sections.push({
        isHeading: false,
        isList: false,
        content: currentParagraph
      });
    }

    return { title: documentTitle, sections };
  };

  // Generate and download the Excel file
  const generateExcel = async () => {
    try {
      setIsGenerating(true);

      // Process content
      const { title: docTitle, sections } = processContent(content);

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Create header data
      const headerData = [
        ['', ''], // Empty row for spacing
        [docTitle, ''], // Title row
        ['', ''], // Empty row for spacing
        ['Fecha:', new Date().toLocaleDateString('es-ES')],
        ['Empresa:', companyName],
        ['', ''] // Empty row for spacing
      ];

      // Create content data
      const contentData = sections.map(section => {
        if (section.isHeading) {
          return ['', section.content]; // Headings with indent
        } else if (section.isList) {
          return ['', '• ' + section.content]; // List items with bullet
        } else {
          return ['', section.content]; // Regular paragraphs
        }
      });

      // Create footer data
      const footerData = [
        ['', ''], // Empty row for spacing
        ['', `Documento generado automáticamente | © ${new Date().getFullYear()} ${companyName}`]
      ];

      // Combine all data
      const allData = [...headerData, ...contentData, ...footerData];

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(allData);

      // Set column widths
      const colWidths = [{ wch: 5 }, { wch: 100 }]; // First column narrow, second wide
      ws['!cols'] = colWidths;

      // Apply styles (limited in xlsx library)
      // We'll use cell merging for the title
      ws['!merges'] = [
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } } // Merge title row across columns
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Informe');

      // Generate Excel file
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      XLSX.writeFile(wb, `informe-${timestamp}.xlsx`);
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Error al generar el informe Excel');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-3 text-green-700">Informe Excel Empresarial</h3>
      <p className="text-sm text-gray-600 mb-4">
        Genera una hoja de cálculo Excel con formato profesional, ideal para datos estructurados,
        análisis y presentaciones empresariales.
      </p>

      <div className="flex flex-col space-y-3">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Título:</span>
          <span className="text-sm text-gray-600">{title || 'Documento Empresarial'}</span>
        </div>

        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Empresa:</span>
          <span className="text-sm text-gray-600">{companyName}</span>
        </div>

        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Contenido:</span>
          <span className="text-sm text-gray-600 truncate">{content.substring(0, 50)}...</span>
        </div>
      </div>

      <button
        onClick={generateExcel}
        disabled={isGenerating}
        className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-70"
      >
        {isGenerating ? (
          'Generando Excel...'
        ) : (
          <>
            <FileSpreadsheet className="w-5 h-5 mr-2" />
            Descargar Informe Excel
          </>
        )}
      </button>
    </div>
  );
};